
import * as React from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils/cn';

export interface IdentifierInputProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit?: (identifier: string) => void;
  error?: string;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  renderForm?: boolean; // Whether to render as a standalone form or just input
}

/**
 * IdentifierInput Component
 * 
 * Specialized input component for user identifiers (email, username, etc.)
 * Handles validation state, error display, and form submission
 */
export function IdentifierInput({
  value,
  onChange,
  onSubmit,
  error,
  placeholder = "Enter your email",
  disabled = false,
  className,
  renderForm = true,
}: IdentifierInputProps) {
  const [localValue, setLocalValue] = React.useState(value);
  const [isValid, setIsValid] = React.useState(false);

  // Email validation
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^\S+@\S+\.\S+$/;
    return emailRegex.test(email);
  };

  React.useEffect(() => {
    setIsValid(validateEmail(localValue));
  }, [localValue]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isValid && onSubmit) {
      onSubmit(localValue);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setLocalValue(newValue);
    onChange(newValue);
  };

  const inputElement = (
    <div className="space-y-2">
      <Input
        type="email"
        value={localValue}
        onChange={handleInputChange}
        placeholder={placeholder}
        disabled={disabled}
        autoFocus={renderForm} // Only autofocus if it's a standalone form
        className={cn(
          error && "border-destructive focus-visible:ring-destructive"
        )}
        aria-invalid={!!error}
        aria-describedby={error ? "identifier-error" : undefined}
      />
      {error && (
        <p 
          id="identifier-error" 
          className="text-sm text-destructive"
          role="alert"
        >
          {error}
        </p>
      )}
    </div>
  );

  if (!renderForm) {
    return (
      <div className={cn("space-y-2", className)}>
        {inputElement}
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className={cn("space-y-4", className)}>
      {inputElement}
      
      {onSubmit && (
        <Button 
          type="submit" 
          className="w-full" 
          disabled={disabled || !isValid}
        >
          {disabled ? 'Please wait...' : 'Continue'}
        </Button>
      )}
    </form>
  );
}